import React, { useState } from 'react';
import './FiveMovementsSection.css';

export const defaultSteps = [
  {
    n: "01",
    title: "Comprehensive medical history",
    body: "We begin with your personal and family history — the unglamorous foundation every useful plan is built on. Nothing is rushed to fit a 12-minute slot.",
  },
  {
    n: "02",
    title: "Multidisciplinary assessment",
    body: "An executive assessment under one roof. Your results are read together, not mailed to you as a pile of PDFs from strangers.",
  },
  {
    n: "03",
    title: "A personal health strategy",
    body: "Findings are synthesized into a tailored action plan: what to change, what to watch, and what to leave alone. Risk mitigation and performance sit in the same document.",
  },
  {
    n: "04",
    title: "Ongoing care and monitoring",
    body: "Monthly follow-ups. The team adjusts as your life changes and keeps you accountable to the goals you actually set — not a generic wellness score.",
  },
  {
    n: "05",
    title: "Continuous support",
    body: "From prescription renewals to an acute concern, or health navigation while you are abroad. Members are not left to assemble the system themselves.",
  },
];

function FiveMovementsSection({
  steps = defaultSteps,
  tagline = "FIVE MOVEMENTS",
  headingLine1 = "A clear path",
  headingLine2 = "toward ",
  headingItalic = "your",
  headingLine3 = "optimal health.",
  introText = "A step-by-step, data-informed method. Not a spa day with a lab coat. The point is a plan you can still recognize a year later.",
  consultationLink = "/book-a-consultation/membership"
}) {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStep = steps[activeStepIndex] || steps[0];

  return (
    <section className="ttc-movements-section py-5">
      <div className="container py-lg-4">
        <div className="row g-4 align-items-start">
          
          {/* Left Column: Heading & Intro Text */}
          <div className="col-12 col-lg-4 pe-lg-4">
            <div className="ttc-movements-tagline text-uppercase mb-3 d-flex align-items-center gap-2">
              <span className="ttc-movements-bullet">•</span>
              <span>{tagline}</span>
            </div>

            <h2 className="ttc-movements-heading mb-4">
              {headingLine1} <br />
              {headingLine2} <span className="ttc-movements-italic">{headingItalic}</span> <br />
              {headingLine3}
            </h2>

            <p className="ttc-movements-intro mb-0">
              {introText}
            </p>
          </div>

          {/* Right Column: Step Tabs & Card Layout */}
          <div className="col-12 col-lg-8">
            
            {/* Top Interactive Tab Bar (Step 01 - 05) */}
            <div className="ttc-steps-tab-bar mb-4 pb-2">
              {steps.map((step, idx) => {
                const isActive = idx === activeStepIndex;
                return (
                  <button
                    key={step.n}
                    onClick={() => setActiveStepIndex(idx)}
                    className={`ttc-step-tab-btn border-0 text-start ${isActive ? 'active' : ''}`}
                    type="button"
                  >
                    <span className="ttc-step-tab-label d-block mb-1">
                      STEP {step.n}
                    </span>
                    {/* Removed text-truncate to allow full text display */}
                    <span className="ttc-step-tab-title d-block">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Main Details & Last Step CTA Row */}
            <div className="row g-4">
              
              {/* Active Step Content Card */}
              <div className="col-12 col-md-7">
                <div className="ttc-step-detail-card p-4 p-md-5 h-100 d-flex flex-column justify-content-center">
                  <span className="ttc-detail-step-number text-uppercase mb-3">
                    STEP {activeStep.n}
                  </span>
                  <h3 className="ttc-detail-step-title mb-3">
                    {activeStep.title}
                  </h3>
                  <p className="ttc-detail-step-body mb-0">
                    {activeStep.body}
                  </p>
                </div>
              </div>

              {/* Last Step CTA Card */}
              <div className="col-12 col-md-5">
                <div className="ttc-last-step-card p-4 p-md-5 h-100 d-flex flex-column justify-content-between">
                  <div>
                    <span className="ttc-last-step-tag text-uppercase mb-3 d-block">
                      LAST STEP
                    </span>
                    <h3 className="ttc-last-step-title text-white mb-3">
                      Curious to learn more?
                    </h3>
                    <p className="ttc-last-step-desc mb-4">
                      A call with the team can answer what a website should not pretend to. Bring the questions. We’ll bring the chart of how the year actually works.
                    </p>
                  </div>

                  <div>
                    <a 
                      href={consultationLink} 
                      className="ttc-last-step-btn d-inline-flex align-items-center gap-2 text-decoration-none"
                    >
                      <span>Book a consultation</span>
                      <span className="ttc-last-step-arrow">→</span>
                    </a>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </div>
    </section>
  );
}

export default FiveMovementsSection;